// B6 Pagos: por cliente y por periodo. «Marcar como recibido» es lo que libera las fichas en el portal.
import { useMutation } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { useDataProvider, useNotify, useRefresh } from "ra-core";
import { Create } from "@/components/admin/create";
import { NumberInput } from "@/components/admin/number-input";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleForm } from "@/components/admin/simple-form";
import { TextInput } from "@/components/admin/text-input";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { dinero, fechaCorta, hoyISO, MES_LARGO } from "@/reclutamiento/logica";
import type { Pago } from "@/reclutamiento/tipos";
import { Encabezado, Kpi, Pildora } from "../comun";
import { useClientes, usePagos } from "../datos";

export const BotonMarcarRecibido = ({ pago }: { pago: Pago }) => {
  const dp = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { mutate, isPending } = useMutation({
    mutationFn: () => (dp as any).marcarPagoRecibido(pago.id),
    onSuccess: () => {
      notify("Pago recibido: las fichas de ese cliente ya se ven completas en su portal", { type: "info" });
      refresh();
    },
    onError: (e: any) => notify(e?.message ?? "No se pudo marcar", { type: "error" }),
  });
  return (
    <Button size="sm" onClick={() => mutate()} disabled={isPending} id={`pagar-${pago.id}`}>
      <Check className="size-4" /> Marcar como recibido
    </Button>
  );
};

export const PagosLista = () => {
  const { data: pagos, isPending } = usePagos();
  const { data: clientes } = useClientes();
  const hoy = hoyISO();
  const mes = MES_LARGO[Number(hoy.slice(5, 7)) - 1];
  const cobradoMes = pagos.filter((p) => p.estado === "pagado" && (p.pagado_el ?? "").slice(0, 7) === hoy.slice(0, 7)).reduce((a, p) => a + Number(p.monto), 0);
  const pendientes = pagos.filter((p) => p.estado === "pendiente");
  const porCobrar = pendientes.reduce((a, p) => a + Number(p.monto), 0);
  const filas = [...pagos].sort((a, b) => (a.estado === "pendiente" ? -1 : 1) - (b.estado === "pendiente" ? -1 : 1) || b.vence.localeCompare(a.vence));
  return (
    <div>
      <Encabezado
        ceja="Por cliente y por periodo"
        titulo="Pagos"
        sub="«Marcar como recibido» es lo que libera las fichas en el portal de ese cliente."
        acciones={<Button asChild size="sm" variant="outline"><Link to="/pagos/create">Nuevo periodo</Link></Button>}
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Kpi titulo={`Cobrado en ${mes.toLowerCase()}`} valor={dinero(cobradoMes)} />
        <Kpi titulo="Por cobrar" valor={dinero(porCobrar)} tono={porCobrar ? "bad" : undefined} detalle={`${pendientes.length} periodo(s) pendiente(s)`} />
        <Kpi titulo="Cuenta para transferir" valor={<span className="text-base">Se configura en «ajustes de la agencia»</span>} detalle="El cliente la ve en su portal, sección Cuenta." />
      </div>
      {isPending ? <p className="text-sm text-muted-foreground">Cargando…</p> : null}
      <div className="overflow-x-auto rounded-xl border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
            <tr><th className="px-3 py-2">Cliente</th><th className="px-3 py-2">Periodo</th><th className="px-3 py-2">Monto</th><th className="px-3 py-2">Vence</th><th className="px-3 py-2">Estado</th><th className="px-3 py-2"><span className="sr-only">Acción</span></th></tr>
          </thead>
          <tbody>
            {filas.map((p) => {
              const vencido = p.estado === "pendiente" && p.vence < hoy;
              return (
                <tr key={p.id} className="border-t">
                  <td className="px-3 py-2 font-semibold">
                    <Link className="hover:underline" to={`/clientes/${p.cliente_id}/show`}>{clientes.find((c) => c.id === p.cliente_id)?.nombre ?? "—"}</Link>
                  </td>
                  <td className="px-3 py-2">{p.periodo}<span className="block text-xs text-muted-foreground">{fechaCorta(p.periodo_inicio)} – {fechaCorta(p.periodo_fin)}</span></td>
                  <td className="num px-3 py-2">{dinero(p.monto)}</td>
                  <td className={`num px-3 py-2 ${vencido ? "text-[var(--bad-ink)] font-semibold" : ""}`}>{fechaCorta(p.vence)}{vencido ? " · vencido" : ""}</td>
                  <td className="px-3 py-2">{p.estado === "pagado" ? <Pildora tono="ok">Recibido {fechaCorta(p.pagado_el)}</Pildora> : <Pildora tono="revisar">Pendiente</Pildora>}</td>
                  <td className="px-3 py-2 text-right">{p.estado === "pendiente" ? <BotonMarcarRecibido pago={p} /> : null}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const transformarPago = (d: any) => {
  const ini: string = d.periodo_inicio;
  const fin = new Date(ini + "T12:00:00");
  fin.setDate(fin.getDate() + 29);
  const [y, m] = ini.split("-").map(Number);
  return {
    ...d,
    periodo: d.periodo || `${MES_LARGO[m - 1]} ${y}`,
    periodo_fin: d.periodo_fin || fin.toISOString().slice(0, 10),
    vence: d.vence || ini,
    estado: "pendiente",
    monto: Number(d.monto) || 0,
  };
};

export const PagoAlta = () => {
  const { data: clientes } = useClientes();
  return (
    <Create transform={transformarPago} redirect="list" title="Nuevo periodo de pago">
      <SimpleForm defaultValues={{ periodo_inicio: hoyISO() }}>
        <SelectInput source="cliente_id" label="Cliente" choices={clientes.map((c) => ({ id: c.id, name: c.nombre }))} isRequired />
        <TextInput source="periodo_inicio" label="Inicio del periodo (AAAA-MM-DD)" isRequired />
        <TextInput source="periodo" label="Nombre del periodo (se llena solo)" />
        <NumberInput source="monto" label="Monto (MXN)" isRequired />
        <TextInput source="vence" label="Vence (AAAA-MM-DD; por omisión el inicio)" />
        <TextInput source="referencia" label="Referencia de la transferencia" />
      </SimpleForm>
    </Create>
  );
};

export default { list: PagosLista, create: PagoAlta };
